import { Manufacturer, Vehicle } from 'api3';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild, inject, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService } from '@app/services/auth.service';
import { PrimeNgMessageService } from '@app/services/primeNgMessage.service';
import { DisplayModel, DisplayVehicle } from '@app/services/types';
import { VehicleService } from '@app/services/vehicle.service';
import { TranslocoPipe } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { Button } from 'primeng/button';

@Component({
  selector: 'app-catalog-selection',
  standalone: true,
  imports: [CommonModule, TranslocoPipe, FormsModule, AutoCompleteModule, Button],
  templateUrl: './catalog-selection.component.html',
})
export class CatalogSelectionComponent implements OnInit {
  onSelect = output<DisplayVehicle | undefined>();
  onHintClick = output();

  isLoading = signal(false);

  makes = signal<Manufacturer[]>([]);
  models = signal<DisplayModel[]>([]);
  vehicles = signal<DisplayVehicle[]>([]);

  filteredMakes = signal<Manufacturer[]>([]);
  filteredModels = signal<DisplayModel[]>([]);
  filteredVehicles = signal<DisplayVehicle[]>([]);

  selectedMake = signal<string | Manufacturer | undefined>(undefined);
  selectedModel = signal<string | DisplayModel | undefined>(undefined);
  selectedVehicle = signal<string | DisplayVehicle | undefined>(undefined);

  @ViewChild('inputMake') inputMake!: AutoComplete;
  @ViewChild('inputModel') inputModel!: AutoComplete;
  @ViewChild('inputVehicle') inputVehicle!: AutoComplete;
  @ViewChild('submitButtonCatalog') submitButton!: Button;

  authService = inject(AuthService);
  vehiclesService = inject(VehicleService);
  messageService = inject(PrimeNgMessageService);

  ngOnInit(): void {
    this._loadMakes();
  }

  public clear() {
    this.inputMake.clear();
    this.selectedMake.set(undefined);
    this.inputModel.clear();
    this.selectedModel.set(undefined);
    this.inputVehicle.clear();
    this.selectedVehicle.set(undefined);
  }

  public hideDropdown() {
    this.inputMake.hide();
    this.inputModel.hide();
    this.inputVehicle.hide();
  }

  /**
   * Makes
   */
  onMakeInputKeyUp(event: KeyboardEvent): void {
    const inputValue = this.inputMake.inputEL?.nativeElement.value;
    if (!inputValue || inputValue.length === 0) {
      this.clearMake();
      const filteredMakesDiffer = this.filteredMakes() !== this.makes() || this.filteredMakes().length !== this.makes().length;
      this.filteredMakes.set(this.makes());
      this.inputMake.show();
      if (filteredMakesDiffer && event.key !== 'Tab') {
        this._selectFirstItem(this.inputMake, this.filteredMakes());
      }
    }
  }

  onMakeInputFocussed(): void {
    this.inputModel.hide();
    this.inputVehicle.hide();

    if (this.selectedMake()) {
      return;
    }

    if (this.makes().length > 0 && !this.inputMake.overlayVisible) {
      this.filteredMakes.set(this.makes());
      this.inputMake.show();
      this._selectFirstItem(this.inputMake, this.filteredMakes());
    }
  }

  clearMake() {
    this.inputModel.hide();
    this.inputVehicle.hide();

    this.selectedMake.set(undefined);
    this.selectedModel.set(undefined);
    this.selectedVehicle.set(undefined);
    this.filteredModels.set([]);
    this.filteredVehicles.set([]);

    this.inputMake.clear();
    this.inputModel.clear();
    this.inputVehicle.clear();

    setTimeout(() => {
      this.inputMake.inputEL?.nativeElement.focus();
    }, 0);
  }

  filterMakes(event: AutoCompleteCompleteEvent) {
    this.isLoading.set(true);
    if (!event.query || event.query.length === 0) {
      this.filteredMakes.set(this.makes());
      this._selectFirstItem(this.inputMake, this.filteredMakes());
      return;
    }
    const lowerCasedQuery = event.query.toLowerCase();
    this.filteredMakes.set(this.makes().filter((make) => make.name.toLowerCase().includes(lowerCasedQuery)));
    this._selectFirstItem(this.inputMake, this.filteredMakes());
  }

  async onSelectMake() {
    this.isLoading.set(true);

    this.inputMake.hide();
    this.selectedModel.set(undefined);
    this.filteredModels.set([]);
    this.inputModel.hide();
    this.selectedVehicle.set(undefined);
    this.inputVehicle.hide();
    this.filteredVehicles.set([]);

    try {
      await this._loadModels(this.selectedMake() as Manufacturer);
    } finally {
      this.isLoading.set(false);
    }

    setTimeout(() => {
      this.inputModel.inputEL?.nativeElement.focus();
      this.inputModel.show();
    }, 500);
  }

  /**
   * Models
   */
  filterModels(event: AutoCompleteCompleteEvent) {
    if (!event) {
      this.filteredModels.set(this.models());
      return;
    }
    const filterValue = event.query.toLowerCase();
    this.filteredModels.set(
      this.models().filter((model) => {
        const infoString = model.displayString || model.name;
        return infoString.toLowerCase()?.includes(filterValue);
      }),
    );
    this._selectFirstItem(this.inputModel, this.filteredModels());
  }

  onModelInputKeyUp(event: KeyboardEvent) {
    const inputValue = this.inputModel.inputEL?.nativeElement.value;
    if (!inputValue || inputValue.length === 0) {
      this.clearModel();

      const filteredModelsDiffer = this.filteredModels() !== this.models() || this.filteredModels().length !== this.models().length;
      this.filteredModels.set(this.models());
      this.inputModel.show();
      if (filteredModelsDiffer && event.key !== 'Tab') {
        this._selectFirstItem(this.inputModel, this.filteredModels());
      }
    }
  }

  onModelInputFocussed() {
    this._selectFirstItem(this.inputModel, this.filteredModels());
  }

  async onSelectModel() {
    this.isLoading.set(true);

    this.selectedVehicle.set(undefined);
    this.inputVehicle.hide();
    this.filteredVehicles.set([]);

    try {
      await this._loadVehicles(this.selectedModel() as DisplayModel);
    } finally {
      this.isLoading.set(false);
    }

    setTimeout(() => {
      this.inputVehicle.inputEL?.nativeElement.focus();
      this.inputVehicle.show();
    }, 300);
  }

  clearModel() {
    this.selectedModel.set(undefined);
    this.inputModel.clear();
    this.inputModel.show();
    this.inputVehicle.hide();
    this.inputVehicle.clear();
    this.selectedVehicle.set(undefined);
    this.filteredVehicles.set([]);
  }

  /**
   * Vehicles
   */
  filterVehicles(event: AutoCompleteCompleteEvent | undefined) {
    this.isLoading.set(true);

    if (!event) {
      this.filteredVehicles.set(this.vehicles());
      this._selectFirstItem(this.inputVehicle, this.filteredVehicles());
      return;
    }

    const filterValue = event.query.toLowerCase();
    this.filteredVehicles.set(
      this.vehicles().filter((vehicle) => {
        const vehicleString = vehicle.displayString || vehicle.name;
        return vehicleString.toLowerCase()?.includes(filterValue);
      }),
    );
    this._selectFirstItem(this.inputVehicle, this.filteredVehicles());
  }

  onVehicleInputKeyUp(event: KeyboardEvent) {
    const inputValue = this.inputVehicle.inputEL?.nativeElement.value;
    if (!inputValue || inputValue.length === 0) {
      this.clearVehicle();

      const filteredVehiclesDiffer = this.filteredVehicles() !== this.vehicles() || this.filteredVehicles().length !== this.vehicles().length;
      this.filteredVehicles.set(this.vehicles());
      this.inputVehicle.show();
      if (filteredVehiclesDiffer && event.key !== 'Tab') {
        this._selectFirstItem(this.inputVehicle, this.filteredVehicles());
      }
    }
  }

  onSelectVehicle() {
    setTimeout(() => {
      this.inputVehicle.hide();
      const button = this.submitButton.el?.nativeElement.querySelector('button');
      button?.focus();
    }, 200);
  }

  onVehicleInputFocussed() {
    this._selectFirstItem(this.inputVehicle, this.filteredVehicles());
  }

  clearVehicle() {
    this.selectedVehicle.set(undefined);
    this.inputVehicle.clear();
    this.inputVehicle.show();
  }

  /**
   * Final selection
   * (add identification data)
   */
  onFinishSelection() {
    const vehicleToSubmit: DisplayVehicle = {
      ...(this.selectedVehicle() as Vehicle),
      vehicleIdentification: {
        catalogue: { make: this.selectedMake() as Manufacturer, model: this.selectedModel() as DisplayModel },
        id: { ...(this.selectedVehicle() as Vehicle).id },
      },
    };
    this.onSelect.emit(vehicleToSubmit);
  }

  private _selectFirstItem(input: AutoComplete, result: Manufacturer[] | DisplayModel[] | DisplayVehicle[]) {
    if (result.length > 0) {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          input.focusedOptionIndex.set(0);
        });
      } else {
        setTimeout(() => {
          input.focusedOptionIndex.set(0);
        }, 0);
      }
    }
    this.isLoading.set(false);
  }

  private async _loadMakes() {
    const { data: manufacturers, error } = await this.vehiclesService.getMakes();

    if (error) {
      console.error('Error loading makes:', error);
      return;
    }

    this.makes.set(manufacturers || []);
    this.filteredMakes.set(manufacturers || []);
  }

  private async _loadModels(make: Manufacturer) {
    const { data: models, error } = await this.vehiclesService.getModels(make.id);

    if (error) {
      return this._handleLoadError(error as HttpErrorResponse);
    }

    this.models.set(
      (models || []).map((model) => {
        return {
          ...model,
          displayString: this.vehiclesService.getModelString(model, make.name),
        };
      }),
    );
    this.filteredModels.set(this.models());
  }

  private async _loadVehicles(model: DisplayModel) {
    const make = this.selectedMake() as Manufacturer;

    const { data: vehicles, error } = await this.vehiclesService.getVehiclesByModelId(make.id, model.id);

    if (error) {
      return this._handleLoadError(error as HttpErrorResponse);
    }

    this.vehicles.set(
      (vehicles || []).map((vehicle) => ({
        ...vehicle,
        displayString: this.vehiclesService.getTypeString(vehicle as Vehicle, make.name),
      })),
    );

    this.filteredVehicles.set(this.vehicles());
    this._selectFirstItem(this.inputVehicle, this.filteredVehicles());
  }

  private async _handleLoadError(error: HttpErrorResponse) {
    this.authService.handleNotLoggedInError(error);

    await this.messageService.showError('vehicleSearch.error');

    console.error('[CatalogSelectionComponent] Error occurred while loading data:', error.message);
    Sentry.captureException(error, {
      tags: {
        location: 'CatalogSelectionComponent',
        feature: 'VehicleIdentification',
        message: 'Error loading make/model/vehicle data in catalog search.',
      },
    });

    const sentryError = new Error(`Error finding vehicle by catalog: ${error?.message || 'Unknown error'}`);
    Sentry.captureException(sentryError, {
      tags: {
        location: 'VinKbaSearchComponent',
        feature: 'VehicleIdentification',
        message: 'Error finding vehicle by catalog',
      },
      extra: {
        originalError: error,
        searchType: 'catalog',
        statusCode: (error as unknown as any)?.statusCode,
        path: (error as unknown as any)?.path,
      },
    });
  }
}
